import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowUpRight, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";

import { useApi } from "@/api";
import { EmptyRow, Section } from "@/components/manage/Section";
import { PageHeader } from "@/components/manage/PageHeader";
import { StatStrip } from "@/components/manage/StatStrip";
import { useActiveOrgId, useAdminMemberships } from "@/components/manage/org-context";

export const Route = createFileRoute("/manage/workspaces/")({
  component: WorkspacesPage,
});

interface WorkspaceRow {
  id: string;
  owner_type: "user" | "org";
  owner_id: string;
  name: string;
  slug: string;
  icon: string | null;
  settings: Record<string, unknown>;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

function WorkspacesPage() {
  const api = useApi();
  const orgId = useActiveOrgId();
  const qc = useQueryClient();
  // Create-workspace is org-admin/owner only (backend B5 is the hard gate).
  // useAdminMemberships() is org_owner/org_admin only (C4).
  const isOrgAdmin = useAdminMemberships().length > 0;

  const workspaces = useQuery({
    queryKey: ["admin-workspaces", orgId],
    queryFn: () => api.request<WorkspaceRow[]>(`/admin/orgs/${orgId}/workspaces`),
  });

  const deleteWorkspace = useMutation({
    mutationFn: (id: string) =>
      api.request<void>(`/admin/workspaces/${id}`, { method: "DELETE" }, false),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin-workspaces", orgId] });
      toast.success("Workspace deleted");
    },
    onError: (err: { message?: string }) => {
      toast.error(err.message ?? "Failed to delete");
    },
  });

  const total = workspaces.data?.length ?? 0;
  const updatedToday =
    workspaces.data?.filter((w) => {
      const d = new Date(w.updated_at);
      const now = new Date();
      return (
        d.getFullYear() === now.getFullYear() &&
        d.getMonth() === now.getMonth() &&
        d.getDate() === now.getDate()
      );
    }).length ?? 0;
  const newestSlug = workspaces.data
    ? [...workspaces.data].sort(
        (a, b) => +new Date(b.created_at) - +new Date(a.created_at),
      )[0]?.slug
    : undefined;

  return (
    <div className="mx-auto max-w-[960px] px-10 py-10">
      <PageHeader
        eyebrow="Admin · Spaces"
        title="Workspaces"
        description="Create workspaces and grant users access. Datasets get assigned per workspace."
        actions={isOrgAdmin ? <CreateWorkspaceDialog orgId={orgId} /> : undefined}
      />

      <StatStrip
        items={[
          { label: "Total", value: total, hint: "in this org" },
          {
            label: "Updated today",
            value: updatedToday,
            hint: "recent edits",
          },
          {
            label: "Newest",
            value: newestSlug ?? "—",
            hint: "most recent slug",
          },
          {
            label: "Org",
            value: "org",
            hint: "owner type",
          },
        ]}
      />

      {/* data-tour anchor: the demo guided tour spotlights the workspaces
          index table (wrapper div — Section doesn't spread props). */}
      <div data-tour="manage-workspaces">
        <Section title="All workspaces">
          {workspaces.isLoading ? (
            <Skeleton className="h-40 w-full rounded-md" />
          ) : (
            <div className="overflow-hidden rounded-lg border border-[var(--color-hairline)] bg-card">
              <table className="w-full text-sm">
                <thead className="bg-[var(--color-surface-soft)]">
                  <tr className="text-left">
                    <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                      Name
                    </th>
                    <th className="w-44 px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                      Slug
                    </th>
                    <th className="w-32 px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                      Updated
                    </th>
                    <th className="w-10" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--color-hairline-soft)]">
                  {workspaces.data?.length ? (
                    workspaces.data.map((w) => (
                      <tr
                        key={w.id}
                        className="group transition hover:bg-[var(--color-surface)]"
                      >
                        <td className="px-4 py-3">
                          <Link
                            to="/manage/workspaces/$id"
                            params={{ id: w.id }}
                            className="inline-flex items-center gap-2 font-medium text-foreground hover:text-[var(--color-primary)]"
                          >
                            <span>{w.icon ?? "📂"}</span>
                            {w.name}
                            <ArrowUpRight className="size-3 opacity-0 transition group-hover:opacity-60" />
                          </Link>
                        </td>
                        <td className="px-4 py-3">
                          <code className="rounded bg-[var(--color-surface-soft)] px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground">
                            /{w.slug}
                          </code>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                          {new Date(w.updated_at).toLocaleDateString()}
                        </td>
                        <td className="px-2 py-2">
                          {isOrgAdmin && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="size-7 opacity-0 transition group-hover:opacity-100"
                              onClick={() => {
                                if (confirm(`Delete workspace "${w.name}"?`)) {
                                  deleteWorkspace.mutate(w.id);
                                }
                              }}
                              aria-label="Delete workspace"
                            >
                              <Trash2 className="size-3.5 text-muted-foreground" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <EmptyRow colSpan={4}>
                      No workspaces yet — create your first one.
                    </EmptyRow>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Section>
      </div>
    </div>
  );
}

function CreateWorkspaceDialog({ orgId }: { orgId: string }) {
  const api = useApi();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");

  const create = useMutation({
    mutationFn: () =>
      api.request<WorkspaceRow>(`/admin/orgs/${orgId}/workspaces`, {
        method: "POST",
        body: JSON.stringify({ name, owner_type: "org", org_id: orgId }),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["admin-workspaces", orgId] });
      await qc.refetchQueries({ queryKey: ["admin-workspaces", orgId] });
      toast.success("Workspace created");
      setOpen(false);
      setName("");
    },
    onError: (err: { message?: string }) => {
      toast.error(err.message ?? "Create failed");
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger
        render={
          <Button>
            <Plus className="size-4" />
            New workspace
          </Button>
        }
      />
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New workspace</DialogTitle>
          <DialogDescription>
            Workspaces hold canvases and link to datasets.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="ws-name">Name</Label>
          <Input
            id="ws-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Q3 Strategy"
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button disabled={!name || create.isPending} onClick={() => create.mutate()}>
            {create.isPending ? "Creating…" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
